/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { t } from '@sportninja/common/i18n'
import PropTypes from 'prop-types'
import React from 'react'

import colors from '@sportninja/common/constants/appColors'
import { paragraphSmall, weightSemiBold } from '../css'
import Text from '../ts-components/Text'

/**
 * GameOfficials component displays the list of game officials (referees, scorekeepers, etc.)
 * in a modern card layout matching the design system
 */
const GameOfficials = ({
  officials = [],
  title = t('common:gameOfficial_plural', { count: 2 }),
}) => {
  if (!officials || officials.length === 0) {
    return null
  }

  return (
    <div
      css={css`
        align-self: stretch;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 8px;
        display: inline-flex;
      `}
    >
      {/* Header with title */}
      <div
        css={css`
          align-self: stretch;
          justify-content: space-between;
          align-items: center;
          display: inline-flex;
        `}
      >
        <div
          css={css`
            ${paragraphSmall}
            ${weightSemiBold}
            justify-content: center;
            display: flex;
            flex-direction: column;
            color: ${colors.NEUTRAL_50};
            text-transform: uppercase;
            line-height: 16.8px;
            letter-spacing: 1.68px;
            word-wrap: break-word;
          `}
        >
          {title}
        </div>
        <div
          css={css`
            opacity: 0;
            text-align: center;
            justify-content: center;
            display: flex;
            flex-direction: column;
            color: ${colors.PRIMARY};
            font-size: 12px;
            font-family: 'Inter', Arial, Helvetica, sans-serif;
            font-weight: 500;
            line-height: 17.4px;
            word-wrap: break-word;
          `}
        >
          View All
        </div>
      </div>

      {/* Officials list */}
      <div
        css={css`
          align-self: stretch;
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-start;
          gap: 16px;
          display: flex;
        `}
      >
        {officials.map((user = {}, idx) => {
          const firstInitial = user?.nameFirst ? user.nameFirst?.charAt(0) : ''
          const lastInitial = user?.nameLast ? user.nameLast?.charAt(0) : ''
          const initials = `${firstInitial}${lastInitial}`

          return (
            <div
              key={`${user.fullName}-${user.description}-${idx}`}
              css={css`
                align-self: stretch;
                padding: 8px;
                background: ${colors.SECONDARY_300};
                border-radius: 8px;
                outline: 1px ${colors.SECONDARY_200} solid;
                outline-offset: -1px;
                justify-content: flex-start;
                align-items: center;
                gap: 8px;
                display: inline-flex;
              `}
            >
              {/* Official image/avatar */}
              <div
                css={css`
                  width: 40px;
                  height: 40px;
                  position: relative;
                  overflow: hidden;
                  border-radius: 40px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  background-color: ${colors.NEUTRAL_100};
                `}
              >
                {user.imageId ? (
                  <img
                    src={user.imageId}
                    css={css`
                      width: 40px;
                      height: 40px;
                      border-radius: 40px;
                    `}
                  />
                ) : (
                  <div
                    css={css`
                      ${paragraphSmall}
                      ${weightSemiBold}
                      width: 40px;
                      height: 40px;
                      border-radius: 40px;
                      background-color: ${colors.NEUTRAL_100};
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      color: ${colors.NEUTRAL_600};
                      align-self: center;
                    `}
                  >
                    {initials || '?'}
                  </div>
                )}
              </div>

              {/* Official details */}
              <div
                css={css`
                  flex-direction: column;
                  justify-content: center;
                  align-items: flex-start;
                  display: inline-flex;
                `}
              >
                <Text
                  variant='paragraphMedium'
                  weight='bold'
                  color={colors.NEUTRAL_0}
                >
                  {user.fullName || 'No Name'}
                </Text>
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  {user.officialType}
                </Text>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

GameOfficials.propTypes = {
  officials: PropTypes.arrayOf(
    PropTypes.shape({
      fullName: PropTypes.string,
      nameFirst: PropTypes.string,
      nameLast: PropTypes.string,
      description: PropTypes.string,
      imageId: PropTypes.string,
    })
  ),
  title: PropTypes.string,
}

export default GameOfficials
